<!--eslint-disable vue/camelcase-->
<template>
  <div
    v-click-outside="onClickOutside"
    class="team-select-root relative inline-block text-center px-3 w-100"
    :class="{ 'team-select-root--open': open }"
  >
    <!-- Loading placeholder when user not yet loaded (e.g. agency dashboard) -->
    <div v-if="!user" class="inline-flex align-items-center justify-between round-xl px-3 py-2 text-sm font-medium text-muted w-100">
      <div class="flex items-center gap-2">
        <i class="uil uil-building text-gray-400"></i>
        <span class="truncate">Loading...</span>
      </div>
    </div>
    <div v-else>
      <!-- Dropdown Trigger -->
      <a
        ref="trigger"
        class="inline-flex align-items-center justify-between round-xl px-3 py-2 text-sm font-medium hover:bg-gray-50 focus:outline-none w-100"
        href="#"
        @click.prevent="toggleOpen"
      >
        <div class="flex items-center gap-2">
          <i class="uil uil-building text-gray-400"></i>
          <span class="truncate text-muted">{{ selectedTeamName }}</span>
        </div>
        <i class="uil" :class="open ? 'uil-angle-up' : 'uil-angle-down'"></i>
      </a>

      <!-- Portaled to document.body when open (escapes #wrapper overflow:hidden) -->
      <div
        v-show="open"
        ref="dropdownPanel"
        class="team-select-dropdown round-xl bg-white shadow-lg"
        :style="dropdownPanelStyle"
        @mousedown.stop
        @click.stop
      >
        <div class="team-select-dropdown-header">
          <div class="px-3 py-2 text-sm font-medium text-gray-700 border-b mb-1 text-left">
            {{ currentBusinessName }}
          </div>
          <hr class="my-0">

          <div v-if="adminOptions.length" class="border-b mb-1 pb-1">
            <div
              v-for="item in adminOptions"
              :key="item.label"
              class="flex items-center px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-gray-100"
              @click="item.action"
            >
              <i :class="item.icon + ' mr-2 text-gray-500'"></i>
              {{ item.label }}
            </div>
          </div>
          <hr v-if="adminOptions.length" class="my-0">

          <div class="px-3 py-1 text-xs font-semibold text-gray-400 uppercase text-left">
            Switch Business
          </div>
        </div>

        <div
          ref="teamList"
          class="team-select-list"
          :style="teamListStyle"
        >
          <div
            v-for="team in availableTeams"
            :key="team.id"
            class="flex items-center px-3 py-2 text-sm rounded-md cursor-pointer transition-colors hover:bg-blue-50 hover:text-blue-700"
            :class="{ 'bg-blue-50 text-blue-700': team.id === selectedTeamId }"
            @click="() => selectTeam(team)"
          >
            <i class="uil uil-building mr-2 text-gray-400"></i>
            <span class="truncate">{{ team.name }}</span>
          </div>
        </div>

        <div class="team-select-dropdown-footer">
          <hr class="my-0">
          <div
            class="flex items-center px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-gray-100"
            @click="logout"
          >
            <i class="uil uil-sign-out-alt mr-2 text-gray-500"></i>
            Log Out
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userUsesNewUiEligible } from '@utils/userUsesNewUiEligible'

const DROPDOWN_MIN_WIDTH = 256
const VIEWPORT_PADDING = 8
const FOOTER_RESERVE_PX = 52

export default {
  data() {
    return {
      open: false,
      loadingStopImpersonate: false,
      loadingImpersonate: false,
      origin: window.location.origin,
      dropdownPanelStyle: {},
      teamListStyle: {},
      dropdownAttachedToBody: false,
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    userRole() {
      return this.user ? this.user.role : "";
    },
    isImpersonating() {
      return this.$store.getters["auth/isImpersonating"];
    },
    isOnAgencyRoute() {
      const name = this.$route?.name;
      const path = this.$route?.path;
      return (
        (name && (String(name).startsWith("agency.") || String(name).startsWith("new-agency."))) ||
        (path && path.startsWith("/agency/"))
      );
    },
    availableTeams() {
      if (!this.user) return [];
      let teams = [];
      if (this.isImpersonating) {
        teams =
          JSON.parse(localStorage.getItem("existing_teams")) || this.user.teams || [];
      } else {
        teams = this.user.teams || [];
      }
      return teams.filter(
        (team, index, self) =>
          index === self.findIndex((t) => t.id === team.id)
      );
    },
    selectedTeamId: {
      get() {
        if (!this.user) return null;
        if (!this.user.business) return null;
        const matchedTeam = this.availableTeams.find(
          (team) => team.business_id === this.user.business.id
        );
        return matchedTeam ? matchedTeam.id : null;
      },
      set(newTeamId) {
        this.user.current_team_id = newTeamId;
      },
    },
    selectedTeamName() {
      if (
        this.user?.role === "Business" &&
        this.isOnAgencyRoute &&
        this.user?.business?.owner?.agency?.name
      ) {
        return this.user.business.owner.agency.name;
      }
      const selected = this.availableTeams.find(
        (t) => t.id === this.selectedTeamId
      );
      return selected ? selected.name : "Select Business";
    },
    currentBusinessName() {
      if (this.user?.role === "Agency" && this.user?.agency?.name) {
        return this.user.agency.name;
      }
      if (
        this.user?.role === "Business" &&
        this.isOnAgencyRoute &&
        this.user?.business?.owner?.agency?.name
      ) {
        return this.user.business.owner.agency.name;
      }
      return this.user?.business?.business_name || "Your Business";
    },
    adminOptions() {
      const options = [];
      if (
        this.user?.role === "Business" &&
        this.isOnAgencyRoute &&
        this.user?.business
      ) {
        options.push({
          label: "Go to Business Dashboard",
          icon: "uil uil-store",
          action: () => this.goToBusinessDashboard(),
        });
      } else if (this.isImpersonating) {
        const originalRole = (localStorage.getItem("main_access_role") || "").toLowerCase();
        if (originalRole === "admin") {
          options.push({
            label: "Go to Super Admin",
            icon: "uil uil-building",
            action: () => this.stopImpersonate(),
          });
        } else if (originalRole === "team") {
          const storedTeams = JSON.parse(localStorage.getItem("existing_teams") || "[]");
          if (Array.isArray(storedTeams) && storedTeams.length > 1) {
            options.push({
              label: "Return to Locations",
              icon: "uil uil-building",
              action: () => this.returnToTeamLocations(),
            });
          }
        } else {
          options.push({
            label: "Return to Agency",
            icon: "uil uil-building",
            action: () => this.returnToAgencyDashboard(),
          });
        }
      } else if (
        this.user?.business?.owner?.agency &&
        this.user?.role === "Business" &&
        Number(this.user.id) === Number(this.user.business.owner_id ?? this.user.business.owner?.id)
      ) {
        options.push({
          label: "Go to Agency Dashboard",
          icon: "uil uil-building",
          action: () => this.goToAgencyDashboard(),
        });
      }
      return options;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.attachDropdownToBody();
          this.updateDropdownPosition();
        });
        window.addEventListener("resize", this.updateDropdownPosition);
        window.addEventListener("scroll", this.updateDropdownPosition, true);
      } else {
        this.detachDropdownFromBody();
        window.removeEventListener("resize", this.updateDropdownPosition);
        window.removeEventListener("scroll", this.updateDropdownPosition, true);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDropdownPosition);
    window.removeEventListener("scroll", this.updateDropdownPosition, true);
    this.detachDropdownFromBody();
  },
  methods: {
    toggleOpen() {
      this.open = !this.open;
    },
    onClickOutside(event) {
      if (!this.open) return;
      const panel = this.$refs.dropdownPanel;
      if (panel && event?.target && panel.contains(event.target)) return;
      this.open = false;
    },
    attachDropdownToBody() {
      const panel = this.$refs.dropdownPanel;
      if (!panel || this.dropdownAttachedToBody) return;
      this._dropdownHome = panel.parentNode;
      document.body.appendChild(panel);
      this.dropdownAttachedToBody = true;
    },
    detachDropdownFromBody() {
      const panel = this.$refs.dropdownPanel;
      if (!panel || !this.dropdownAttachedToBody || !this._dropdownHome) return;
      this._dropdownHome.appendChild(panel);
      this.dropdownAttachedToBody = false;
      this.dropdownPanelStyle = {};
      this.teamListStyle = {};
    },
    updateDropdownPosition() {
      const trigger = this.$refs.trigger;
      const panel = this.$refs.dropdownPanel;
      if (!this.open || !trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      const width = Math.max(rect.width, DROPDOWN_MIN_WIDTH);
      const spaceBelow =
        window.innerHeight - rect.bottom - VIEWPORT_PADDING - FOOTER_RESERVE_PX;
      const spaceAbove = rect.top - VIEWPORT_PADDING - FOOTER_RESERVE_PX;
      const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
      const listMaxHeight = Math.max(
        120,
        Math.min(320, openUpward ? spaceAbove - 80 : spaceBelow - 80)
      );

      const top = openUpward
        ? Math.max(VIEWPORT_PADDING, rect.top - listMaxHeight - 80 - FOOTER_RESERVE_PX)
        : rect.bottom + VIEWPORT_PADDING;

      this.dropdownPanelStyle = {
        position: "fixed",
        left: `${Math.min(rect.left, window.innerWidth - width - VIEWPORT_PADDING)}px`,
        top: `${top}px`,
        width: `${width}px`,
        zIndex: 9999,
      };
      this.teamListStyle = {
        maxHeight: `${listMaxHeight}px`,
      };
    },
    agencyDashboardRoute(user) {
      const u = user || this.user;
      return userUsesNewUiEligible(u) ? "new-agency.dashboard" : "agency.dashboard";
    },
    async goToAgencyDashboard() {
      this.open = false;
      this.$store.commit("auth/loading", true);
      try {
        await this.$store.dispatch("auth/getProfile");
        const u = this.$store.getters["auth/user"];
        await this.$router.push({ name: this.agencyDashboardRoute(u) });
      } finally {
        this.$store.commit("auth/loading", false);
      }
    },
    goToBusinessDashboard() {
      this.open = false;
      const name = userUsesNewUiEligible(this.user) ? "new-business.dashboard" : "business.dashboard";
      this.$router.push({ name });
    },
    async returnToAgencyDashboard() {
      this.open = false;
      this.loadingStopImpersonate = true;
      try {
        await this.$store.dispatch("auth/stopImpersonate");
        this.$store.commit("auth/loading", true);
        await this.$store.dispatch("auth/getProfile");
        const u = this.$store.getters["auth/user"];
        const dash = this.agencyDashboardRoute(u);
        if (this.$route.name !== dash) {
          await this.$router.push({ name: dash });
        }
      } catch (err) {
        console.error("Return to agency failed:", err);
      } finally {
        this.loadingStopImpersonate = false;
        this.$store.commit("auth/loading", false);
      }
    },
    async returnToTeamLocations() {
      this.open = false;
      this.loadingStopImpersonate = true;
      try {
        await this.$store.dispatch("auth/stopImpersonate");
        this.$store.commit("auth/loading", true);
        await this.$store.dispatch("auth/getProfile");
        if (this.$route.name !== "team.dashboard") {
          await this.$router.push({ name: "team.dashboard" });
        }
      } catch (err) {
        console.error("Return to locations failed:", err);
      } finally {
        this.loadingStopImpersonate = false;
        this.$store.commit("auth/loading", false);
      }
    },
    async selectTeam(team) {
      this.open = false;
      this.selectedTeamId = team.id;
      await this.impersonateViaTeam();
    },
    async impersonateViaTeam() {
      const selectedTeam = this.availableTeams.find(
        (team) => team.id === this.user.current_team_id
      );
      if (!selectedTeam || !selectedTeam.owner_id) {
        console.error("Invalid team selection");
        return;
      }
      this.loadingImpersonate = true;
      try {
        await this.$store.dispatch("auth/impersonateTeam", selectedTeam.id);
        this.$store.commit("auth/loading", true);
        await this.$store.dispatch("auth/getProfile");
        const u = this.$store.getters["auth/user"];
        const dash = userUsesNewUiEligible(u) ? "new-business.dashboard" : "business.dashboard";
        if (this.$route.name !== dash) {
          await this.$router.push({ name: dash });
        }
      } catch (error) {
        console.error("Impersonation failed:", error);
      } finally {
        this.loadingImpersonate = false;
        this.$store.commit("auth/loading", false);
      }
    },
    stopImpersonate() {
      this.loadingStopImpersonate = true;
      this.$store
        .dispatch("auth/stopImpersonate")
        .then(() =>
          this.$store.dispatch("auth/getProfile").then(() => {
            this.$router.push("/" + this.userRole.toLowerCase());
            this.loadingStopImpersonate = false;
          })
        )
        .catch(() => {
          this.loadingStopImpersonate = false;
        });
    },
    logout() {
      this.open = false;
      this.$store.dispatch('auth/logout').then(() => {
        this.$router.push({ name: 'login' });
      });
    },
  },
};
</script>

<style scoped>
.team-select-root--open {
  position: relative;
  z-index: 200;
}

.team-select-dropdown {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.08);
  outline: none;
}

.team-select-list {
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.team-select-dropdown-footer {
  flex-shrink: 0;
}

button:focus {
  outline: none;
}
</style>
